import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Motion";
import { PROJECTS } from "../data/portfolio";

const titleCase = (s = "") =>
  s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

const FILTERS = [
  { key: "all", label: "All" },
  { key: "keynote", label: "Keynotes" },
  { key: "web", label: "Web" },
];

function Card({ project }) {
  return (
    <Link to={`/work/${project.slug}`} data-testid={`work-card-${project.slug}`} className="group block h-full">
      <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0C0C0C] aspect-[4/3]">
        <div className="absolute inset-0 transition-transform duration-[900ms] ease-out group-hover:scale-[1.04] will-change-transform">
          <img
            src={project.cover}
            alt={project.coverAlt || project.title}
            loading="lazy"
            draggable="false"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute inset-0 border border-transparent group-hover:border-white/20 rounded-[24px] transition-colors duration-500 pointer-events-none" />
        <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-9">
          <div className="flex justify-between items-start">
            <span
              data-testid={`work-tag-${project.slug}`}
              className="text-[11px] uppercase tracking-[0.2em] text-white/85 border border-white/25 bg-black/30 backdrop-blur rounded-full px-3 py-1.5"
            >
              {project.tag}
            </span>
            <span className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 bg-white/5 backdrop-blur">
              <ArrowUpRight size={17} />
            </span>
          </div>
          <div>
            <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{project.title}</h3>
            {project.subtitle && <p className="text-white/60 text-base md:text-lg mt-2">{titleCase(project.subtitle)}</p>}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function FeaturedWork() {
  const [filter, setFilter] = useState("all");
  const items = PROJECTS.filter((p) => filter === "all" || p.discipline === filter);

  return (
    <section id="work" className="relative py-24 md:py-36 scroll-mt-20" data-testid="featured-work">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Selected work</p>
            <h2 className="font-display font-bold text-4xl md:text-6xl tracking-tight max-w-2xl leading-[0.95]">
              Work that makes brands <span className="font-serif-accent italic font-normal">look good</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="work-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`work-filter-${f.key}`}
                aria-pressed={filter === f.key}
                className={`rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                  filter === f.key
                    ? "bg-white text-black border-white"
                    : "bg-white/5 border-white/15 text-white/70 hover:text-white hover:border-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 gap-6 md:gap-8">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.div
                key={p.slug}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card project={p} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
